import type { ExperienceVariable } from '../experience/variables.js';
import { experienceSegmentsOf, experienceVariableToken } from '../experience/variables.js';
import { assertNever } from '../lib/assert-never.js';
import { renderVariables, variableToken } from '../protocol/variables.js';
import type {
  DisplayCondition,
  RichText,
  RichTextInline,
  RichTextNode,
} from '../schemas/experience.js';

/**
 * The PRIMITIVES of a duplication (refonte modulaire §4.2, grill of 2026-09-21;
 * issue #115): the table that says which local id became which, and the
 * rewriting of one text's tokens through it.
 *
 * ## The rule, written once, for everybody
 *
 * A local id is an ADDRESS. `{{objectif:<id>}}`, `{{critere:<id>}}`,
 * `{{champ:<blockId>}}`, `{{choix:<pageId>}}` (§8.6), the step a « aller à une
 * étape » aims at and the step a fallback falls back to (§7.2, §8.4) are all
 * one thing: a reference from one node of a content to another. So duplicating
 * a subtree has exactly one rule, and every clone of `clone.ts` obeys it:
 *
 * > Mint a fresh id for every node of the copied subtree. Every reference that
 * > points INSIDE the subtree is readdressed to the new id. Every reference
 * > that points OUTSIDE is left exactly as it was. Nothing outside the subtree
 * > is touched.
 *
 * ## Why there is no universal walker
 *
 * The tempting shape - one recursive function over `unknown` that rewrites
 * every string that looks like an id - was rejected, and not on style. It is
 * `any` in disguise: it would happily rewrite a criterion's ENTITY id, a media
 * asset id or a version tag the day one of them lands in a table, and nothing
 * in the type system could see the mistake. What is here instead is small and
 * blind: a `Map`, and a rewriting that only ever asks the map about an id it
 * has already read as a token of a KNOWN kind. Above it, the clones name every
 * field they copy, so a field added to a schema is a compile error there rather
 * than a reference this file silently forgot.
 *
 * ## Why the tables are one per NAMESPACE, and never one flat map
 *
 * A step id, an objective id, a condition id, a page id and a block id are five
 * namespaces. Nothing in `refuseDuplicateIds` (`schemas/protocol.ts`,
 * `schemas/experience.ts`) forbids a condition id equal to an objective id -
 * each is checked against its OWN set - so a single flat table would let a
 * coincidence readdress a `{{objectif:...}}` to a condition's new id. Uuids
 * make that coincidence unthinkable in practice, which is precisely why the bug
 * would be unthinkable to find. One table per namespace costs three `Map`s and
 * removes the question.
 *
 * ## Why the token readers are reused and never re-implemented
 *
 * `renderVariables` (§8.6, `protocol/variables.ts`) and `experienceSegmentsOf`
 * (`experience/variables.ts`) already read the tokens - one regular expression
 * per dialect, written once so that « what the editor inserted » and « what the
 * server counts » cannot drift. A second regular expression here would be a
 * third spelling of the same thing, and the first one to fall behind a new kind
 * of variable. `renderVariables` also happens to carry the exact fallback this
 * rule needs: a token its resolver cannot answer is left AS IS, which is
 * « points outside the subtree, so do not touch it » spelled in the other
 * direction.
 */

/** Old local id -> the one minted for the copy, within ONE namespace. */
export type IdRemap = ReadonlyMap<string, string>;

/**
 * One fresh id per DISTINCT id given, in the order they come.
 *
 * A repeated id is minted once: two nodes sharing an id is a defect
 * `refuseDuplicateIds` refuses at the wire, and minting twice for it would put
 * a second defect on top of the first - the copy would hold two nodes the
 * references can no longer tell apart either.
 *
 * `mintId` is an ARGUMENT and never `crypto.randomUUID` called here: it is what
 * keeps the whole of this module pure, and therefore testable with a counter
 * rather than with a mock of a global. The front hands it
 * `newLocalId` / `crypto.randomUUID` (`frontend/src/lib/protocol-ids.ts`,
 * `frontend/src/lib/page-editor.ts`), which is the same shape of id the stores
 * mint server side.
 */
export function buildIdRemap(ids: Iterable<string>, mintId: () => string): IdRemap {
  const remap = new Map<string, string>();
  for (const id of ids) {
    if (remap.has(id)) continue;
    remap.set(id, mintId());
  }
  return remap;
}

/**
 * The id the copy carries, or the id itself when it points outside the subtree.
 *
 * The whole « leave what points outside intact » half of the rule is this one
 * `??`. It is a function rather than an inlined `?? id` at each call site
 * because reading `remapId(...)` at thirteen places says « this is a reference,
 * and it follows the rule », where a bare `.get()` would leave a reader
 * wondering whether the `undefined` case was considered.
 */
export function remapId(remap: IdRemap, id: string): string {
  return remap.get(id) ?? id;
}

/* -------------------------------------------------------- the two dialects */

/**
 * A protocol text, with every `{{objectif:...}}` that names a COPIED objective
 * readdressed (§7.2, §8.6).
 *
 * A `{{critere:...}}` is never touched, and that is not an omission: the id
 * inside it is a criterion ENTITY id (§4), which no duplication of a step ever
 * mints. Asking the objectives table about it would work by luck today - the
 * table only holds objective ids - and break the day the caller widens what it
 * passes.
 */
export function remapProtocolTokens(text: string, objectives: IdRemap): string {
  return renderVariables(text, (variable) => {
    if (variable.kind !== 'objective') return null;
    const minted = objectives.get(variable.id);
    return minted === undefined ? null : variableToken({ kind: 'objective', id: minted });
  });
}

/**
 * The two tables an experience's tokens follow (§8.6): a `{{champ:...}}`
 * carries the id of its BLOCK, a `{{choix:...}}` the id of its PAGE. An
 * `{{objectif:...}}` and a `{{critere:...}}` on a page name objects of another
 * content entirely and follow nothing.
 */
export type ExperienceTokenRemap = {
  /** What `{{champ:<blockId>}}` follows. */
  readonly fields: IdRemap;
  /** What `{{choix:<pageId>}}` follows. */
  readonly choices: IdRemap;
};

/** An experience text, with its field and choice tokens readdressed. */
export function remapExperienceTokens(text: string, remap: ExperienceTokenRemap): string {
  return experienceSegmentsOf(text)
    .map((segment) =>
      segment.kind === 'text'
        ? segment.text
        : experienceVariableToken(remapExperienceVariable(segment.variable, remap)),
    )
    .join('');
}

function remapExperienceVariable(
  variable: ExperienceVariable,
  remap: ExperienceTokenRemap,
): ExperienceVariable {
  switch (variable.kind) {
    case 'field':
      return { kind: 'field', id: remapId(remap.fields, variable.id) };
    case 'choice':
      return { kind: 'choice', id: remapId(remap.choices, variable.id) };
    case 'objective':
    case 'criterion':
    // `@noyau` carries no address at all (N12): there is one retained noyau per
    // experience, and a copy of a page or of a block cites the same one.
    case 'noyau':
      return variable;
    default:
      // The KIND and not the variable: `ExperienceVariable` is one object type
      // and not a union, so the switch narrows the property and never the whole
      // value. The id rides in the message instead, because a token nobody can
      // read is worth nothing without the means of finding the text it sits in.
      return assertNever(variable.kind, `experience variable kind of ${variable.id}`);
  }
}

/**
 * A DISPLAY CONDITION of a copied block (§9.2 of the refonte), readdressed by
 * the same rule as a token: it cites a field by the id of its block, a choice by
 * the id of its page and of its buttons - three addresses the two tables follow
 * - and an objective or the noyau, which point outside any page and follow
 * nothing.
 *
 * The buttons of a choice go through the BLOCK table, which is what `fields`
 * is: it maps every block of the copied subtree, whatever `{{champ:...}}` would
 * have made of it.
 */
export function remapDisplayCondition(
  condition: DisplayCondition,
  remap: ExperienceTokenRemap,
): DisplayCondition {
  switch (condition.kind) {
    case 'noyau':
      return { kind: 'noyau', operator: condition.operator, noyaux: [...condition.noyaux] };
    case 'choice':
      return {
        kind: 'choice',
        pageId: remapId(remap.choices, condition.pageId),
        operator: condition.operator,
        buttonIds: condition.buttonIds.map((id) => remapId(remap.fields, id)),
      };
    case 'checkbox':
      return {
        kind: 'checkbox',
        blockId: remapId(remap.fields, condition.blockId),
        operator: condition.operator,
      };
    case 'input':
      return {
        kind: 'input',
        blockId: remapId(remap.fields, condition.blockId),
        operator: condition.operator,
      };
    case 'objective':
      return {
        kind: 'objective',
        objectiveId: condition.objectiveId,
        operator: condition.operator,
      };
    default:
      return assertNever(condition, 'display condition');
  }
}

/**
 * The same rewriting, on the tree a Texte block stores (§9.2).
 *
 * A variable is NOT a node of that tree: it stays a token inside a run's text,
 * which is what lets one regular expression read a whole page. So this walk is
 * only about reaching the runs - it maps the closed list of node types and
 * rewrites nothing else, and `assertNever` makes a seventh node type a compile
 * error rather than a paragraph whose tokens quietly stop being readdressed.
 */
export function remapRichTextTokens(document: RichText, remap: ExperienceTokenRemap): RichText {
  return { nodes: document.nodes.map((node) => remapRichTextNode(node, remap)) };
}

function remapRichTextNode(node: RichTextNode, remap: ExperienceTokenRemap): RichTextNode {
  switch (node.type) {
    case 'paragraph':
      return {
        type: 'paragraph',
        children: node.children.map((inline) => remapRichTextInline(inline, remap)),
      };
    case 'bullet-list':
      return {
        type: 'bullet-list',
        items: node.items.map((item) => ({
          children: item.children.map((inline) => remapRichTextInline(inline, remap)),
        })),
      };
    default:
      return assertNever(node, 'rich text node');
  }
}

function remapRichTextInline(inline: RichTextInline, remap: ExperienceTokenRemap): RichTextInline {
  switch (inline.type) {
    case 'text':
      return {
        type: 'text',
        text: remapExperienceTokens(inline.text, remap),
        marks: [...inline.marks],
        color: inline.color,
      };
    case 'break':
      return { type: 'break' };
    default:
      return assertNever(inline, 'rich text inline');
  }
}
